import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 8
const MEMORY_SIZE = 0x0800
const CHANNEL_START = 0x0010
const CHANNEL_SIZE = 16
const CHANNEL_COUNT = 99
const MAGIC = Array.from('\u0002PROGRA', (character) =>
  character.charCodeAt(0),
)

type FD268Spec = {
  id: string
  model: string
  summary: string
  fingerprint: readonly number[]
}

export type FD268Profile = {
  protocol: 'fd268'
  id: string
  vendor: 'Feidaxin'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 99
  nameLength: 0
  memorySize: number
  fingerprint: readonly number[]
  powerLevels: readonly ['High']
}

const SPECS: readonly FD268Spec[] = [
  {
    id: 'feidaxin-fd-268a',
    model: 'FD-268A',
    summary: 'Feidaxin FD-268A VHF handheld.',
    fingerprint: [0xff, 0xee, 0x46, 0xff],
  },
  {
    id: 'feidaxin-fd-268b',
    model: 'FD-268B',
    summary: 'Feidaxin FD-268B UHF handheld.',
    fingerprint: [0xff, 0xee, 0x47, 0xff],
  },
  {
    id: 'feidaxin-fd-288a',
    model: 'FD-288A',
    summary: 'Feidaxin FD-288A VHF handheld.',
    fingerprint: [0xff, 0xee, 0x4b, 0xff],
  },
  {
    id: 'feidaxin-fd-288b',
    model: 'FD-288B',
    summary: 'Feidaxin FD-288B UHF handheld.',
    fingerprint: [0xff, 0xee, 0x4c, 0xff],
  },
  {
    id: 'feidaxin-fd-150a',
    model: 'FD-150A',
    summary: 'Feidaxin FD-150A VHF handheld.',
    fingerprint: [0xff, 0xee, 0x45, 0xff],
  },
  {
    id: 'feidaxin-fd-160a',
    model: 'FD-160A',
    summary: 'Feidaxin FD-160A VHF handheld.',
    fingerprint: [0xff, 0xee, 0x48, 0xff],
  },
  {
    id: 'feidaxin-fd-450a',
    model: 'FD-450A',
    summary: 'Feidaxin FD-450A UHF handheld.',
    fingerprint: [0xff, 0xee, 0x44, 0xff],
  },
  {
    id: 'feidaxin-fd-460a',
    model: 'FD-460A',
    summary: 'Feidaxin FD-460A UHF handheld.',
    fingerprint: [0xff, 0xee, 0x4a, 0xff],
  },
  {
    id: 'feidaxin-fd-460uh',
    model: 'FD-460UH',
    summary: 'Feidaxin FD-460UH wideband UHF handheld.',
    fingerprint: [0xff, 0xf4, 0x44, 0xff],
  },
]

export const FD268_PROFILES: readonly FD268Profile[] = SPECS.map((spec) => ({
  protocol: 'fd268',
  id: spec.id,
  vendor: 'Feidaxin',
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '99 channels', 'No channel names'],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: 0,
  memorySize: MEMORY_SIZE,
  fingerprint: spec.fingerprint,
  powerLevels: ['High'],
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'High',
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function rawBcdValue(raw: Uint8Array): number | null {
  if (raw.every((value) => value === 0xff)) return null
  let value = 0
  for (let index = raw.length - 1; index >= 0; index -= 1) {
    value = value * 100 + (raw[index] >> 4) * 10 + (raw[index] & 0x0f)
  }
  return value
}

function decimalBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = 0; index < size; index += 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = rawBcdValue(raw)
  if (value === null || value === 0 || value === 16665) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 12000) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - 12000, polarity: 'R' }
  }
  if (value >= 8000) {
    return { mode: 'dtcs', ctcss: null, dtcs: value - 8000, polarity: 'N' }
  }
  return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): Uint8Array {
  if (channel.toneMode === 'none') return Uint8Array.of(0xff, 0xff)
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    return decimalBcd(Math.round(channel.ctcss * 10), 2)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    if (!DTCS_CODES.includes(channel.dtcs as (typeof DTCS_CODES)[number])) {
      throw new Error('That DCS code is not supported by this radio.')
    }
    const result = decimalBcd(channel.dtcs, 2)
    result[1] |= channel.dtcsPolarity === 'R' ? 0xc0 : 0x80
    return result
  }
  return Uint8Array.of(0xff, 0xff)
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

export function parseFD268Channel(
  image: Uint8Array,
  profile: FD268Profile,
  number: number,
): WebChannel {
  const offset = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: 'FM',
    power: 'High',
    skip: false,
  }
}

export function parseFD268Channels(
  image: Uint8Array,
  profile: FD268Profile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, index) => parseFD268Channel(image, profile, index + 1),
  )
}

export function applyFD268Channel(
  image: Uint8Array,
  profile: FD268Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error('Channel must be between 1 and 99.')
  }
  const offset = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: FD268Profile,
  transport: RadioTransport,
): Promise<void> {
  let entered = false
  for (let attempt = 0; attempt < 8 && !entered; attempt += 1) {
    await transport.discardInput()
    for (const value of MAGIC) await transport.write(Uint8Array.of(value))
    try {
      await readAck(transport, '')
      entered = true
    } catch {
      // These radios intentionally require several program-mode attempts.
    }
  }
  if (!entered) throw new Error('The Feidaxin radio did not enter programming mode.')
  await transport.write(new TextEncoder().encode('M'))
  await transport.write(Uint8Array.of(0x02))
  await transport.readExact(8, 3000)
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, 'The radio rejected the Feidaxin clone session.')
}

function commandFrame(command: number, address: number): Uint8Array {
  return Uint8Array.of(
    command,
    (address >> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(commandFrame(0x52, address))
  const response = await transport.readExact(4 + BLOCK_SIZE, 3000)
  await transport.write(Uint8Array.of(ACK))
  await readAck(transport, `The radio rejected read block 0x${address.toString(16)}.`)
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  await transport.write(commandFrame(0x57, address))
  await transport.write(data)
  await readAck(transport, `The radio rejected write block 0x${address.toString(16)}.`)
}

export async function readFD268RadioWithTransport(
  profile: FD268Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading Feidaxin FD-268 memory',
      })
    }
    if (
      profile.fingerprint.some(
        (value, index) => image[index] !== value,
      )
    ) {
      throw new Error(
        `This image does not match the selected Feidaxin ${profile.model}.`,
      )
    }
    return { image, reportedModel: profile.model }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeFD268RadioWithTransport(
  profile: FD268Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than FD-268 memory.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total,
        label: 'Writing Feidaxin FD-268 memory',
      })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
